import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, Copy, Archive, Bookmark, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS, STATUS_COLORS, fmtDateNL, timeStatusColor } from "./shared";

export default function JaceSessionView() {
  const { sessionId } = useParams();
  const [s, setS] = useState(null);

  const load = () => api.get(`/jace/sessions/${sessionId}`).then(({ data }) => setS(data)).catch(() => setS(false));
  useEffect(() => { load(); }, [sessionId]); // eslint-disable-line

  if (s === null) return <JaceLayout><div className="chip text-[#A3A3A3]">Laden…</div></JaceLayout>;
  if (s === false) return <JaceLayout><div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#4A4A4A]">Sessie niet gevonden.</div></JaceLayout>;

  const saveTemplate = async () => {
    try {
      await api.post(`/jace/templates/from-session/${s.session_id}`);
      toast.success("Opgeslagen als template (V1.0)");
    } catch { toast.error("Template opslaan mislukt"); }
  };

  return (
    <JaceLayout>
      <div data-testid="jace-session-view">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="chip mb-2" style={{ color: JACE }}>{s.session_id} · {TYPE_LABELS[s.type]}</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">{s.title || "—"}</h2>
            <div className="chip text-[#A3A3A3] mt-2">{fmtDateNL(s.date)} {s.start_time} · {s.level} · {s.location || "—"}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <span className="chip px-3 py-2 border" style={{ color: STATUS_COLORS[s.status], borderColor: `${STATUS_COLORS[s.status]}55` }} data-testid="jace-session-status">{s.status}</span>
            {(s.status === "READY" || s.status === "IN PROGRESS") && (
              <Link to={`/jace/play/${s.session_id}`} className="px-4 py-2 chip inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-play-btn">▶ PLAY</Link>
            )}
            {["COMPLETED", "REVIEWED"].includes(s.status) && (
              <Link to={`/jace/feedback/new?session=${s.session_id}`} className="px-4 py-2 chip inline-flex items-center gap-2 border transition-colors" style={{ borderColor: JACE, color: JACE }} data-testid="jace-session-feedback-btn"><FileText size={12}/> FEEDBACK</Link>
            )}
            <Link to={`/jace/sessions/${s.session_id}/edit`} className="px-4 py-2 chip inline-flex items-center gap-2 border border-[#F5F3F0]/25 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-edit-btn"><Pencil size={12}/> BEWERK</Link>
            <button onClick={saveTemplate} className="px-4 py-2 chip inline-flex items-center gap-2 border border-[#F5F3F0]/25 hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors" data-testid="jace-save-template-btn"><Bookmark size={12}/> TEMPLATE</button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <div className="border border-[#F5F3F0]/15 p-4"><div className="chip text-[#A3A3A3]">TOTAL PLANNED</div><div className="font-display text-3xl mt-1">{s.total_planned_time} MIN</div></div>
          <div className="border border-[#F5F3F0]/15 p-4"><div className="chip text-[#A3A3A3]">TARGET</div><div className="font-display text-3xl mt-1">{s.planned_duration} MIN</div></div>
          <div className="border border-[#F5F3F0]/15 p-4"><div className="chip text-[#A3A3A3]">TIME STATUS</div><div className="font-display text-3xl mt-1" style={{ color: timeStatusColor(s.time_status) }} data-testid="jace-time-status">{s.time_status}</div></div>
          <div className="border border-[#F5F3F0]/15 p-4"><div className="chip text-[#A3A3A3]">PROGRAMMED BY</div><div className="font-display text-xl mt-1 uppercase truncate">{s.programmed_by || "—"}</div></div>
        </div>

        {s.primary_objective && <div className="mb-8"><div className="chip text-[#A3A3A3] mb-2">— PRIMARY OBJECTIVE</div><p className="text-[#DDD6CB]">{s.primary_objective}</p></div>}

        {(s.objectives || []).length > 0 && (
          <div className="mb-8" data-testid="jace-objectives">
            <div className="chip text-[#A3A3A3] mb-2">— SESSION OBJECTIVES</div>
            {s.objectives.map((o, i) => (
              <div key={i} className="flex gap-4 py-2 border-b border-[#F5F3F0]/10">
                <span className="chip" style={{ color: JACE }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[#DDD6CB] text-sm">{o.title}</span>
              </div>
            ))}
          </div>
        )}

        <div className="chip text-[#A3A3A3] mb-3">— TRAINING BLOCKS</div>
        {(s.blocks || []).length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]">Nog geen blokken. Open de builder via BEWERK.</div>
        ) : (
          <div className="line-t line-dark" data-testid="jace-blocks">
            {s.blocks.map((b, i) => (
              <div key={b.id || i} className="line-b line-dark py-4">
                <div className="flex items-center gap-4">
                  <span className="chip" style={{ color: JACE }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-xl uppercase flex-1">{b.name}</span>
                  <span className="chip text-[#A3A3A3]">{b.duration || 0} MIN</span>
                </div>
                {b.coach_note && <div className="chip text-[#D4A93A] mt-1 ml-10">COACH NOTE // {b.coach_note}</div>}
                {(b.exercises || []).map((e, j) => (
                  <div key={e.id || j} className="ml-10 mt-2 flex items-center gap-4 text-sm">
                    <span className="text-[#DDD6CB] flex-1">{e.name}</span>
                    {e.detail && <span className="chip text-[#A3A3A3]">{e.detail}</span>}
                    <span className="chip text-[#4A4A4A]">{e.duration || 0} MIN</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {s.coach_notes && <div className="mt-8"><div className="chip text-[#A3A3A3] mb-2">— GENERAL COACH NOTES</div><p className="text-[#DDD6CB] text-sm whitespace-pre-wrap">{s.coach_notes}</p></div>}
        {(s.quick_notes || []).length > 0 && (
          <div className="mt-8" data-testid="jace-quicknotes">
            <div className="chip text-[#A3A3A3] mb-2">— QUICK NOTES</div>
            {s.quick_notes.map((n, i) => <div key={i} className="text-sm text-[#DDD6CB] py-1.5 border-b border-[#F5F3F0]/10">“{n.note}” <span className="chip text-[#4A4A4A]">— {n.by}</span></div>)}
          </div>
        )}
        {s.review && (
          <div className="mt-8 border border-[#F5F3F0]/15 p-5" data-testid="jace-review-summary">
            <div className="chip mb-3" style={{ color: "#4ADE80" }}>// REVIEWED — {s.review.reviewed_by}</div>
            {s.actual_duration && <div className="chip text-[#A3A3A3] mb-3">ACTUAL DURATION: {s.actual_duration} MIN</div>}
            {s.review.what_worked && <div className="mb-2"><span className="chip text-[#4A4A4A]">WORKED // </span><span className="text-sm text-[#DDD6CB]">{s.review.what_worked}</span></div>}
            {s.review.what_needs_improvement && <div className="mb-2"><span className="chip text-[#4A4A4A]">IMPROVE // </span><span className="text-sm text-[#DDD6CB]">{s.review.what_needs_improvement}</span></div>}
            {s.review.next_session_focus && <div><span className="chip text-[#4A4A4A]">NEXT FOCUS // </span><span className="text-sm" style={{ color: JACE }}>{s.review.next_session_focus}</span></div>}
          </div>
        )}
        {s.migrated_from === "JOTS" && <div className="chip text-[#4A4A4A] mt-8">// gemigreerd uit JOTS ({s.legacy_jots_id})</div>}
      </div>
    </JaceLayout>
  );
}
